/**
 * normalizeAuthUser — gives every authenticated-user payload a usable `id`.
 *
 * The API describes the signed-in identity two different ways:
 *
 *   POST /auth/login          → a full user entity, keyed on `id`
 *   GET  /auth/me             → a flat map keyed on `user_id`
 *   POST /auth/login/mobile   → no user at all, so the client MUST call /auth/me
 *
 * Mobile therefore only ever saw the `user_id` shape, and `User.id` — typed as
 * required — was in fact undefined for the whole session. Screens that build a
 * request from it either 400'd on the literal string "undefined" or, worse,
 * guarded on `if (!user?.id) return` and rendered an empty state that looked
 * like real data (My Time showed a blank week instead of your hours).
 *
 * The server now returns both keys, but a client cannot assume the server is
 * newer than it is: a shipped mobile build outlives any given API deploy. So
 * normalize on the way in, at every point where a payload becomes the session
 * user, and let the rest of the app rely on `id` alone.
 */
/**
 * Returns the payload with `id` populated from `user_id` when the server (or a
 * value cached by an older build) omitted it. `user_id` is left in place — some
 * callers still read it, and dropping it would be a second, needless breakage.
 *
 * Returns the input unchanged when it is null/undefined or already has an `id`,
 * so this is safe to apply blindly on every auth response.
 */
export function normalizeAuthUser(raw) {
    if (!raw || typeof raw !== 'object')
        return raw;
    if (raw.id)
        return raw;
    if (!raw.user_id)
        return raw;
    return { ...raw, id: raw.user_id };
}
